import { PoiParameterRequestKind } from "@/cache/GeoJSONCache";
import { networkCaches } from "@/cache/GlobalCache";
import { recordPortfolioEnergyCompleteness, recordPortfolioEnergyRequest } from "@/otel/metrics";
import { roundToNearestFiveMinutes } from "@/time/roundToNearstFiveMinutes";
import { getModelBoundingBox } from "@/weather-parameters/lookup";
import {
  CoordinateSystem,
  type JSONResponseDataPoints,
  type PointRequest,
  SinglePointCoordinate,
  WORLD_BBOX,
} from "@mm/api.meteomatics.com";
import { DateTime, Duration } from "luxon";
import {
  isPortfolioEnergyForecastSiteAvailable,
  requestCachedEnergyForecast,
  requestCachedPortfolioEnergyForecastAvailability,
} from "./energyForecastRequest";
import { type ResolvedPortfolioEnergyAsset, getPortfolioEnergySupport } from "./energyRequests";
import { normalizePortfolioPowerValue } from "./powerValues";
import {
  type PortfolioRequestGroup,
  type PortfolioRequestLocation,
  createPortfolioPointLookup,
  groupPortfolioRequestLocations,
} from "./requests";
import { type Portfolio, PortfolioDataSource, PortfolioType } from "./types";

export type PortfolioEnergyValue = {
  valueMw: number | null;
  state: "loaded" | "missing" | "failed" | "stale";
};

type PortfolioEnergyForecastResult =
  | { state: "loaded"; samples: Array<[timeMillis: number, valueMw: number]> }
  | { state: "failed"; samples: Array<[timeMillis: number, valueMw: number]> };

export type PortfolioEnergyForecasts = ReadonlyMap<string, PortfolioEnergyForecastResult>;

export type PortfolioEnergyRequestLocation = PortfolioRequestLocation;
export type PortfolioEnergyRequestGroup = PortfolioRequestGroup;

export function normalizePortfolioEnergyTime(time: DateTime) {
  return roundToNearestFiveMinutes(time);
}

export function groupPortfolioEnergyRequests(assets: ResolvedPortfolioEnergyAsset[]) {
  return groupPortfolioRequestLocations(assets.flatMap((asset) => (asset.request ? [asset.request] : [])));
}

export function portfolioEnergyValueRequestKey(options: {
  portfolio: Pick<Portfolio, "id" | "idAccount" | "dataSource">;
  requestFingerprint: string;
  model: string;
  time: DateTime;
}) {
  return JSON.stringify([
    "portfolio-energy-values-v1",
    options.portfolio.idAccount ?? null,
    options.portfolio.id,
    options.portfolio.dataSource,
    options.requestFingerprint,
    options.model,
    normalizePortfolioEnergyTime(options.time).toUTC().toISO(),
    "MW",
  ]);
}

/** The Energy Forecast endpoint returns a complete run, so its cache identity is independent of display time. */
export function portfolioEnergyForecastRequestKey(options: {
  portfolio: Pick<Portfolio, "id" | "idAccount" | "dataSource">;
  requestFingerprint: string;
  model: string;
}) {
  return JSON.stringify([
    "portfolio-energy-forecasts-v1",
    options.portfolio.idAccount ?? null,
    options.portfolio.id,
    options.portfolio.dataSource,
    options.requestFingerprint,
    options.model,
    "MW",
  ]);
}

function createPointRequest(group: PortfolioEnergyRequestGroup, model: string, time: DateTime) {
  return {
    coordinates: group.locations.map(
      ({ coordinates }) =>
        new SinglePointCoordinate<CoordinateSystem.WGS84>(coordinates.lat, coordinates.lon, CoordinateSystem.WGS84),
    ),
    startDatetime: time,
    duration: Duration.fromObject({ hours: 1 }),
    temporalResolution: Duration.fromObject({ hours: 1 }),
    height: 1,
    parameters: [group.parameter],
    model,
    ensSelect: "",
    requestOptions: { calibrated: false },
    boundingBoxLimit: getModelBoundingBox(model) ?? WORLD_BBOX,
    width: 1,
  } satisfies PointRequest<CoordinateSystem.WGS84>;
}

function readValue(point: JSONResponseDataPoints | undefined, expectedTime: DateTime): PortfolioEnergyValue {
  if (!point?.dates?.length) {
    return { valueMw: null, state: "missing" };
  }
  const expectedMillis = expectedTime.toMillis();
  const datum = point.dates.find(({ date }) => DateTime.fromISO(date).toMillis() === expectedMillis);
  if (!datum) {
    return { valueMw: null, state: "stale" };
  }
  const valueMw = normalizePortfolioPowerValue(datum.value);
  if (valueMw === null) {
    return { valueMw: null, state: "missing" };
  }
  return { valueMw, state: "loaded" };
}

function mapGroupResponse(
  group: PortfolioEnergyRequestGroup,
  response: JSONResponseDataPoints[],
  expectedTime: DateTime,
) {
  const findPoint = createPortfolioPointLookup(response);
  const values = new Map<string, PortfolioEnergyValue>();
  for (const location of group.locations) {
    const value = readValue(findPoint(location.coordinates), expectedTime);
    for (const assetId of location.assetIds) {
      values.set(assetId, value);
    }
  }
  return values;
}

export type PortfolioEnergyValueLoad = {
  key: string;
  time: DateTime;
  promise: Promise<Map<string, PortfolioEnergyValue>>;
  cancel: () => void;
};

export type PortfolioEnergyForecastLoad = {
  key: string;
  promise: Promise<PortfolioEnergyForecasts>;
  cancel: () => void;
};

function forecastType(portfolio: Pick<Portfolio, "portfolioType">) {
  return portfolio.portfolioType === PortfolioType.Wind ? "wlp" : "slp";
}

function forecastSamples(forecastData: Array<Array<unknown>>) {
  return forecastData.flatMap(([seconds, value]): Array<[number, number]> => {
    const timeMillis = typeof seconds === "number" ? seconds * 1000 : Number.NaN;
    const valueMw = normalizePortfolioPowerValue(value);
    return Number.isFinite(timeMillis) && valueMw !== null ? [[timeMillis, valueMw]] : [];
  });
}

/**
 * Reads the most recent usable Energy Forecast sample at or before `time`.
 * A map must never display a forecast value from the future.
 */
export function selectPortfolioEnergyForecastValues(forecasts: PortfolioEnergyForecasts, time: DateTime) {
  const values = new Map<string, PortfolioEnergyValue>();
  const targetMillis = time.toMillis();
  for (const [assetId, forecast] of forecasts) {
    if (forecast.state === "failed") {
      values.set(assetId, { valueMw: null, state: "failed" });
      continue;
    }
    let selected: [timeMillis: number, valueMw: number] | undefined;
    for (const sample of forecast.samples) {
      if (sample[0] <= targetMillis && (!selected || sample[0] > selected[0])) {
        selected = sample;
      }
    }
    values.set(assetId, selected ? { valueMw: selected[1], state: "loaded" } : { valueMw: null, state: "missing" });
  }
  return values;
}

export function loadPortfolioEnergyForecasts(options: {
  portfolio: Portfolio;
  assets: ResolvedPortfolioEnergyAsset[];
  requestFingerprint: string;
  model: string;
  time: DateTime;
}): PortfolioEnergyForecastLoad {
  const key = portfolioEnergyForecastRequestKey(options);
  const siteAssetIds = new Map<string, string[]>();
  for (const asset of options.assets) {
    if (!asset.configurationIssue && asset.assetId && asset.coordinates && asset.siteId) {
      const assetIds = siteAssetIds.get(asset.siteId) ?? [];
      assetIds.push(asset.assetId);
      siteAssetIds.set(asset.siteId, assetIds);
    }
  }
  let cancelled = false;
  const promise = awaitPortfolioEnergyForecastAvailability()
    .then((availability) => {
      const requests: Array<Promise<readonly [string, PortfolioEnergyForecastResult]>> = [...siteAssetIds].map(
        async ([siteId, assetIds]): Promise<readonly [string, PortfolioEnergyForecastResult]> => {
          const started = performance.now();
          const request = {
            forecastType: forecastType(options.portfolio),
            siteId,
            model: options.model,
          } as const;
          if (availability && !isPortfolioEnergyForecastSiteAvailable(availability, request)) {
            recordPortfolioEnergyRequest({
              assetCount: assetIds.length,
              dataSource: PortfolioDataSource.Energy,
              technology: options.portfolio.portfolioType,
              durationMs: performance.now() - started,
              outcome: "failed",
            });
            return [siteId, { state: "failed", samples: [] }];
          }
          try {
            const response = await requestCachedEnergyForecast(request);
            recordPortfolioEnergyRequest({
              assetCount: assetIds.length,
              dataSource: PortfolioDataSource.Energy,
              technology: options.portfolio.portfolioType,
              durationMs: performance.now() - started,
              outcome: "success",
            });
            return [siteId, { state: "loaded", samples: forecastSamples(response.forecast.forecast_data) }];
          } catch {
            recordPortfolioEnergyRequest({
              assetCount: assetIds.length,
              dataSource: PortfolioDataSource.Energy,
              technology: options.portfolio.portfolioType,
              durationMs: performance.now() - started,
              outcome: "failed",
            });
            return [siteId, { state: "failed", samples: [] }];
          }
        },
      );
      return Promise.all(requests);
    })
    .then((results) => {
      const bySite = new Map(results);
      const forecasts = new Map<string, PortfolioEnergyForecastResult>();
      for (const [siteId, assetIds] of siteAssetIds) {
        const forecast = bySite.get(siteId);
        if (forecast) {
          for (const assetId of assetIds) {
            forecasts.set(assetId, forecast);
          }
        }
      }
      if (!cancelled && forecasts.size) {
        const values = selectPortfolioEnergyForecastValues(forecasts, options.time);
        const count = (state: PortfolioEnergyValue["state"]) =>
          [...values.values()].filter((value) => value.state === state).length;
        recordPortfolioEnergyCompleteness({
          requested: forecasts.size,
          loaded: count("loaded"),
          missing: 0,
          failed: count("failed"),
          stale: 0,
          dataSource: PortfolioDataSource.Energy,
          technology: options.portfolio.portfolioType,
        });
      }
      return forecasts;
    });
  return {
    key,
    promise,
    cancel: () => {
      if (!cancelled) {
        cancelled = true;
      }
    },
  };
}

/** Continue with forecast requests if availability is temporarily unavailable. */
async function awaitPortfolioEnergyForecastAvailability() {
  try {
    return await requestCachedPortfolioEnergyForecastAvailability();
  } catch {
    return undefined;
  }
}

export function loadPortfolioEnergyValues(options: {
  portfolio: Portfolio;
  assets: ResolvedPortfolioEnergyAsset[];
  requestFingerprint: string;
  model: string;
  time: DateTime;
}): PortfolioEnergyValueLoad {
  const time = normalizePortfolioEnergyTime(options.time);
  const key = portfolioEnergyValueRequestKey({ ...options, time });
  const groups =
    getPortfolioEnergySupport(options.portfolio) === "supported" ? groupPortfolioEnergyRequests(options.assets) : [];
  let cancelled = false;
  const requests = groups.map(async (group) => {
    const request = createPointRequest(group, options.model, time);
    const started = performance.now();
    const metric = {
      assetCount: group.locations.reduce((count, location) => count + location.assetIds.length, 0),
      dataSource: options.portfolio.dataSource ?? "unknown",
      technology: options.portfolio.portfolioType,
    };
    try {
      const response = await networkCaches.poi_cache.retrieveParameter(
        request,
        PoiParameterRequestKind.SingleParameterColoring,
      ).asynchronous;
      recordPortfolioEnergyRequest({ ...metric, durationMs: performance.now() - started, outcome: "success" });
      return mapGroupResponse(group, response, time);
    } catch {
      recordPortfolioEnergyRequest({ ...metric, durationMs: performance.now() - started, outcome: "failed" });
      return new Map(
        group.locations
          .flatMap((location) => location.assetIds)
          .map((id) => [id, { valueMw: null, state: "failed" } as const]),
      );
    }
  });

  const requested = groups.reduce(
    (count, group) => count + group.locations.reduce((sum, location) => sum + location.assetIds.length, 0),
    0,
  );
  const promise = Promise.all(requests).then((results) => {
    const values = new Map<string, PortfolioEnergyValue>();
    for (const result of results) {
      for (const [assetId, value] of result) {
        values.set(assetId, value);
      }
    }
    if (!cancelled && requested > 0) {
      const count = (state: PortfolioEnergyValue["state"]) =>
        [...values.values()].filter((value) => value.state === state).length;
      recordPortfolioEnergyCompleteness({
        requested,
        loaded: count("loaded"),
        missing: count("missing"),
        failed: count("failed"),
        stale: count("stale"),
        dataSource: options.portfolio.dataSource ?? "unknown",
        technology: options.portfolio.portfolioType,
      });
    }
    return values;
  });

  return {
    key,
    time,
    promise,
    cancel: () => {
      if (cancelled) {
        return;
      }
      cancelled = true;
    },
  };
}
